import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight, GraduationCap, ClipboardList, Search, PhoneCall, BookOpen, Library,
    Smartphone, CalendarDays, Megaphone, MapPin, ChevronRight, ChevronLeft, Baby, BookOpenText, BookMarked,
    Repeat2, Award, Globe2, Shield, Laptop2, Pill, SquarePen, Briefcase, UtensilsCrossed, School2, Pause, Play,
} from "lucide-react";
import { SectionHeading, DraftNotice } from "@/components/common/SectionHeading";
import SEO, { makeBreadcrumbSchema } from "@/components/common/SEO";
import {
    fetchApplicantTypes, fetchDepartments, fetchPrograms, fetchNews, fetchSettings,
    submitTuitionLead, fetchHeroSlides, fetchCampusPublic, campusImageUrl,
} from "@/lib/lnlApi";
import { toast } from "sonner";

const ICONS = {
    Baby, BookOpenText, BookMarked, GraduationCap, Repeat2, Award, Globe2,
    Shield, Laptop2, Pill, SquarePen, Briefcase, UtensilsCrossed, School2,
};

const APPLICANT_ICON = {
    kindergarten: "Baby", elementary: "BookOpenText", "junior-high": "BookMarked",
    "senior-high": "GraduationCap", "college-freshman": "GraduationCap",
    "college-transferee": "Repeat2", "als-pept": "Award", foreign: "Globe2",
};

export default function Home() {
    const [applicantTypes, setApplicantTypes] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [news, setNews] = useState([]);
    const [settings, setSettings] = useState(null);
    const [spotlight, setSpotlight] = useState([]);
    const [query, setQuery] = useState("");
    const [levelFilter, setLevelFilter] = useState("all");

    useEffect(() => {
        Promise.all([fetchApplicantTypes(), fetchDepartments(), fetchPrograms(), fetchNews(3), fetchSettings()])
            .then(([a, d, p, n, s]) => { setApplicantTypes(a); setDepartments(d); setPrograms(p); setNews(n); setSettings(s); });
        fetchCampusPublic().then((rows) => setSpotlight((rows || []).slice(0, 3))).catch(() => setSpotlight([]));
    }, []);

    const filteredPrograms = programs.filter((p) => {
        const q = query.trim().toLowerCase();
        if (q && !`${p.name} ${(p.career_tags || []).join(" ")}`.toLowerCase().includes(q)) return false;
        if (levelFilter !== "all" && p.level !== levelFilter) return false;
        return true;
    });

    return (
        <div data-testid="home-page">
            <SEO
                title="Career-focused Programs in Urdaneta City"
                description="Explore college and basic-education programs at Lyceum Northern Luzon (LNL) — Urdaneta City, Pangasinan. Start your application today."
                path="/"
            />
            <HeroSection settings={settings} />
            <ApplicantPathsSection applicantTypes={applicantTypes} />
            <ProgramFinderSection
                programs={filteredPrograms}
                departments={departments}
                query={query}
                setQuery={setQuery}
                levelFilter={levelFilter}
                setLevelFilter={setLevelFilter}
            />
            <WhyChooseSection />
            <EnrollmentProcessSection />
            <TuitionCTA />
            <CampusExperienceSection />
            <CommunitySpotlightSection stories={spotlight} />
            <TestimonialsSection />
            <NewsSection news={news} />
            <StudentResourcesSection settings={settings} />
            <CampusLocationSection settings={settings} />
            <FinalCTA />
        </div>
    );
}

function HeroSection({ settings }) {
    return <HeroSlider settings={settings} />;
}

function HeroSchoolName({ settings }) {
    const name = settings?.hero_school_name || settings?.official_name || "Lyceum Northern Luzon";
    return (
        <div className="fade-up flex items-center gap-3 mb-4" data-testid="hero-school-name">
            <span className="h-[2px] w-10 bg-lnl-gold shrink-0" aria-hidden="true" />
            <span className="font-heading font-extrabold uppercase text-lnl-gold-soft tracking-[0.14em] text-lg sm:text-2xl lg:text-3xl leading-tight [text-shadow:0_2px_18px_rgba(0,0,0,0.55)]">{name}</span>
        </div>
    );
}

function HeroSlider({ settings }) {
    const [slides, setSlides] = useState(null);
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const pauseTimerRef = React.useRef(null);
    const prefersReduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    // When the user manually navigates, pause briefly then auto-resume after 15s.
    const pauseThenResume = React.useCallback(() => {
        setPaused(true);
        if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
        pauseTimerRef.current = setTimeout(() => setPaused(false), 15000);
    }, []);
    useEffect(() => () => { if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current); }, []);

    useEffect(() => {
        fetchHeroSlides()
            .then((rows) => setSlides(rows && rows.length ? rows : []))
            .catch(() => setSlides([]));
    }, []);

    // Autoplay: honour per-slide duration
    useEffect(() => {
        if (!slides || slides.length < 2 || paused || prefersReduced) return;
        const dur = Math.max(3, Math.min(20, slides[index]?.duration_seconds || 7)) * 1000;
        const t = setTimeout(() => setIndex((i) => (i + 1) % slides.length), dur);
        return () => clearTimeout(t);
    }, [index, slides, paused, prefersReduced]);

    // Fallback when no slides
    if (!slides) {
        return <div className="hero-frame relative min-h-[480px]" aria-hidden="true"><div className="hero-scrim" /></div>;
    }
    if (slides.length === 0) {
        // Legacy single-image fallback preserves earlier behaviour
        const heroImg = settings?.hero_image_url || "https://lyceumnorthernluzon.edu.ph/images/1920/14104567/1000010347.jpg";
        return (
            <section className="hero-frame relative" data-testid="hero-section" aria-label="Welcome to Lyceum Northern Luzon">
                <div className="hero-kb" style={{ backgroundImage: `url("${heroImg}")` }} aria-hidden="true" />
                <div className="hero-scrim" aria-hidden="true" />
                <div className="hero-grain" aria-hidden="true" />
                <div className="relative lnl-container py-24 md:py-32 lg:py-40 min-h-[560px] md:min-h-[640px] lg:min-h-[720px] flex flex-col justify-end">
                    <HeroSchoolName settings={settings} />
                    <h1 className="display text-white text-[46px] sm:text-[60px] lg:text-[76px]" data-testid="hero-headline">{settings?.hero_headline || "Build Your Future"}</h1>
                    {settings?.hero_subtitle && <p className="mt-5 text-white/85 text-lg md:text-xl max-w-2xl">{settings.hero_subtitle}</p>}
                </div>
            </section>
        );
    }

    const active = slides[index] || slides[0];
    const opacity = Math.max(0.15, Math.min(0.85, Number(active.overlay_opacity ?? 0.55)));
    const gotoPrev = () => { setIndex((i) => (i - 1 + slides.length) % slides.length); pauseThenResume(); };
    const gotoNext = () => { setIndex((i) => (i + 1) % slides.length); pauseThenResume(); };

    return (
        <section className="hero-frame relative" data-testid="hero-section" aria-roledescription="carousel" aria-label="LNL featured slides">
            {/* Crossfade stack: each slide rendered as an absolutely-positioned background */}
            {slides.map((s, i) => (
                <div
                    key={s.id || i}
                    className={`absolute inset-0 transition-opacity ease-out ${i === index ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                    style={{ transitionDuration: "900ms" }}
                    aria-hidden={i !== index}
                    data-testid={`hero-slide-${i}`}
                >
                    <div
                        className={`hero-kb w-full h-full ${prefersReduced ? "!animate-none" : ""}`}
                        style={{
                            backgroundImage: `url("${s.image_url}")`,
                            animationName: prefersReduced ? "none" : (s.motion_style === "pan_left" ? "heroPanLeft" : s.motion_style === "pan_right" ? "heroPanRight" : "kb"),
                        }}
                    />
                    <div className="hero-scrim" style={{ background: `linear-gradient(180deg, rgba(15,26,17,${opacity * 0.75}) 0%, rgba(15,26,17,${opacity * 0.9}) 55%, rgba(15,26,17,${Math.min(opacity + 0.15, 0.95)}) 100%)` }} />
                    <div className="hero-grain" />
                </div>
            ))}

            {/* Foreground content — swaps with slide */}
            <div className="relative lnl-container py-24 md:py-32 lg:py-40 min-h-[560px] md:min-h-[640px] lg:min-h-[720px] flex flex-col justify-end">
                <div key={active.id || index} className="max-w-3xl">
                    <HeroSchoolName settings={settings} />
                    {active.eyebrow && (
                        <div className="fade-up inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white/90 px-3.5 py-1.5 text-[11px] font-heading font-semibold uppercase tracking-[0.18em]" data-testid="hero-eyebrow">
                            <span className="w-1.5 h-1.5 rounded-full bg-lnl-gold animate-pulse" />
                            {active.eyebrow}
                        </div>
                    )}
                    <h1 className="fade-up d1 mt-5 display text-white text-[42px] sm:text-[56px] lg:text-[72px]" data-testid="hero-headline">
                        {active.headline || "Lyceum Northern Luzon"}
                    </h1>
                    {active.subheading && <div className="fade-up d2 mt-3 text-lnl-gold-soft font-heading font-semibold text-base sm:text-lg">{active.subheading}</div>}
                    {active.description && <p className="fade-up d2 mt-5 text-white/85 text-lg md:text-xl max-w-2xl leading-relaxed">{active.description}</p>}
                    <div className="fade-up d3 mt-8 flex flex-wrap items-center gap-3">
                        {active.primary_cta_text && (
                            active.cta_new_tab
                                ? <a href={active.primary_cta_url || "#"} target="_blank" rel="noreferrer" className="lnl-btn-gold" data-testid="hero-primary-cta">{active.primary_cta_text} <ArrowRight className="h-4 w-4" /></a>
                                : <Link to={active.primary_cta_url || "/admissions/apply"} className="lnl-btn-gold" data-testid="hero-primary-cta">{active.primary_cta_text} <ArrowRight className="h-4 w-4" /></Link>
                        )}
                        {active.secondary_cta_text && (
                            active.cta_new_tab
                                ? <a href={active.secondary_cta_url || "#"} target="_blank" rel="noreferrer" className="lnl-btn-ghost-light" data-testid="hero-secondary-cta">{active.secondary_cta_text}</a>
                                : <Link to={active.secondary_cta_url || "/"} className="lnl-btn-ghost-light" data-testid="hero-secondary-cta">{active.secondary_cta_text}</Link>
                        )}
                    </div>
                </div>
            </div>

            {/* Prev / Next controls */}
            {slides.length > 1 && (
                <>
                    <button onClick={gotoPrev} aria-label="Previous slide" data-testid="hero-prev"
                        className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full bg-black/35 hover:bg-black/55 text-white grid place-items-center backdrop-blur border border-white/20 transition-colors">
                        <ChevronLeft className="h-6 w-6" />
                    </button>
                    <button onClick={gotoNext} aria-label="Next slide" data-testid="hero-next"
                        className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full bg-lnl-gold hover:bg-lnl-gold-soft text-lnl-ink grid place-items-center backdrop-blur border border-white/20 transition-colors shadow-lg">
                        <ChevronRight className="h-6 w-6" />
                    </button>
                    {/* Indicators + play/pause */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3">
                        <div className="flex items-center gap-1.5" role="tablist" data-testid="hero-indicators">
                            {slides.map((_, i) => (
                                <button key={i} onClick={() => { setIndex(i); pauseThenResume(); }} role="tab" aria-selected={i === index} aria-label={`Slide ${i + 1}`} data-testid={`hero-dot-${i}`}
                                    className={`h-1.5 rounded-full transition-all ${i === index ? "w-8 bg-lnl-gold" : "w-2.5 bg-white/60 hover:bg-white/90"}`} />
                            ))}
                        </div>
                        <button onClick={() => setPaused((p) => !p)} aria-label={paused ? "Play slideshow" : "Pause slideshow"} data-testid="hero-playpause"
                            className="h-7 w-7 rounded-full grid place-items-center bg-white/10 border border-white/25 text-white/90 hover:bg-white/20">
                            {paused || prefersReduced ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
                        </button>
                    </div>
                </>
            )}
        </section>
    );
}

function ApplicantPathsSection({ applicantTypes }) {
    return (
        <section className="py-16 lg:py-20 bg-white" id="applicant-paths" data-testid="applicant-paths">
            <div className="lnl-container">
                <SectionHeading
                    eyebrow="Choose your applicant path"
                    title="Where are you starting from?"
                    description="Every learner begins differently. Pick the path that matches your journey to see requirements, timelines and a start-application link."
                />
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {applicantTypes.map((a) => {
                        const Icon = ICONS[APPLICANT_ICON[a.code] || "GraduationCap"];
                        return (
                            <Link
                                to={`/admissions/${a.code}`}
                                key={a.code}
                                className="lnl-card p-5 flex flex-col hover:shadow-md hover:-translate-y-0.5 hover:border-lnl-green/40 transition-transform"
                                data-testid={`applicant-card-${a.code}`}
                            >
                                <div className="w-10 h-10 rounded-md bg-lnl-green-bg text-lnl-green-deep grid place-items-center mb-4">
                                    <Icon className="h-5 w-5" />
                                </div>
                                <div className="font-heading font-bold text-lnl-charcoal">{a.label}</div>
                                <div className="text-sm text-lnl-gray mt-1 leading-relaxed">{a.short}</div>
                                <div className="mt-4 pt-3 border-t border-lnl-border text-xs text-lnl-green-deep font-heading font-semibold inline-flex items-center gap-1">
                                    View path <ArrowRight className="h-3.5 w-3.5" />
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function ProgramFinderSection({ programs, departments, query, setQuery, levelFilter, setLevelFilter }) {
    return (
        <section id="program-finder" className="py-16 lg:py-20 bg-lnl-off-white" data-testid="program-finder">
            <div className="lnl-container">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                    <SectionHeading
                        eyebrow="Program finder"
                        title="Choose Your Career Path"
                        description="Search bachelor's programs and basic education levels by keyword or filter by academic unit."
                    />
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            onClick={() => setLevelFilter("all")}
                            className={`text-xs font-heading font-semibold rounded-full px-3 py-1.5 border ${levelFilter === "all" ? "bg-lnl-green text-white border-lnl-green" : "border-lnl-border text-lnl-charcoal bg-white"}`}
                            data-testid="filter-all"
                        >All</button>
                        <button
                            onClick={() => setLevelFilter("college")}
                            className={`text-xs font-heading font-semibold rounded-full px-3 py-1.5 border ${levelFilter === "college" ? "bg-lnl-green text-white border-lnl-green" : "border-lnl-border text-lnl-charcoal bg-white"}`}
                            data-testid="filter-college"
                        >College</button>
                        <button
                            onClick={() => setLevelFilter("basic")}
                            className={`text-xs font-heading font-semibold rounded-full px-3 py-1.5 border ${levelFilter === "basic" ? "bg-lnl-green text-white border-lnl-green" : "border-lnl-border text-lnl-charcoal bg-white"}`}
                            data-testid="filter-basic"
                        >Basic Education</button>
                    </div>
                </div>

                <div className="mt-6 relative max-w-xl">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-lnl-gray" />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search programs, e.g. Criminology, IT, Tourism..."
                        className="w-full pl-10 pr-4 py-3 rounded-md border border-lnl-border bg-white focus:outline-none focus:border-lnl-green"
                        data-testid="program-search"
                        aria-label="Search programs"
                    />
                </div>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {programs.map((p) => {
                        const dept = departments.find((d) => d.slug === p.department_slug);
                        return (
                            <Link
                                to={`/programs/${p.slug}`}
                                key={p.slug}
                                className="lnl-card p-5 flex flex-col hover:border-lnl-green/40 hover:shadow-sm transition-colors"
                                data-testid={`program-card-${p.slug}`}
                            >
                                <div className="text-[11px] uppercase tracking-[0.14em] font-heading font-semibold text-lnl-green-deep">
                                    {dept?.name || p.department_slug}
                                </div>
                                <div className="font-heading font-bold text-lg text-lnl-charcoal mt-1 leading-snug">{p.name}</div>
                                <div className="text-sm text-lnl-gray mt-2 leading-relaxed line-clamp-3">{p.overview}</div>
                                <div className="flex flex-wrap gap-1.5 mt-3">
                                    {(p.career_tags || []).slice(0, 3).map((t) => (
                                        <span key={t} className="text-[10px] font-heading font-semibold text-lnl-gray bg-white border border-lnl-border rounded-full px-2 py-0.5">{t}</span>
                                    ))}
                                </div>
                                <div className="mt-4 pt-3 border-t border-lnl-border flex items-center justify-between text-xs">
                                    <span className="text-lnl-green-deep font-heading font-semibold inline-flex items-center gap-1">View program <ArrowRight className="h-3.5 w-3.5" /></span>
                                    <span className="text-lnl-gray">Ask · Apply</span>
                                </div>
                            </Link>
                        );
                    })}
                    {programs.length === 0 && (
                        <div className="col-span-full text-center text-lnl-gray py-10 border border-dashed border-lnl-border rounded-md">
                            No programs match your search. Try clearing filters.
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

function WhyChooseSection() {
    const blocks = [
        { title: "Career-Focused Education", body: "Programs designed to develop competent and morally upright graduates ready for the workforce.", icon: GraduationCap },
        { title: "Supportive Learning Community", body: "Faculty and staff dedicated to student success and values formation.", icon: BookOpen },
        { title: "Accessible Urdaneta Location", body: "Situated along MacArthur Highway, easily reachable across Pangasinan.", icon: MapPin },
        { title: "Student Services & Resources", body: "Library, OPAC, guidance and a student portal to support your studies.", icon: Library },
        { title: "Basic Education to College", body: "Continuous pathways from Kindergarten to Senior High School to College.", icon: School2 },
        { title: "Admissions Support", body: "Personalized guidance from inquiry through enrollment.", icon: ClipboardList },
    ];
    return (
        <section className="py-16 lg:py-20 bg-white" data-testid="why-choose-section">
            <div className="lnl-container">
                <SectionHeading eyebrow="Why choose LNL" title="An established path toward your future" />
                <DraftNotice tone="info">
                    Verified statistics (board-exam results, accreditations, government recognition, graduate outcomes) are hidden here until the institution confirms them.
                </DraftNotice>
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {blocks.map((b) => (
                        <div key={b.title} className="lnl-card p-5 flex gap-4">
                            <div className="w-10 h-10 rounded-md bg-lnl-green-bg text-lnl-green-deep grid place-items-center shrink-0">
                                <b.icon className="h-5 w-5" />
                            </div>
                            <div>
                                <div className="font-heading font-bold text-lnl-charcoal">{b.title}</div>
                                <div className="text-sm text-lnl-gray mt-1 leading-relaxed">{b.body}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function EnrollmentProcessSection() {
    const steps = [
        "Choose your program or education level",
        "Review the admission requirements",
        "Submit an inquiry or application",
        "Receive assessment and admissions guidance",
        "Confirm enrollment",
    ];
    return (
        <section className="py-16 lg:py-20 bg-lnl-off-white" data-testid="enrollment-process">
            <div className="lnl-container">
                <SectionHeading eyebrow="How it works" title="A clear enrollment journey" description="Five simple steps guide you from first inquiry to a confirmed slot at LNL." />
                <ol className="mt-8 grid grid-cols-1 md:grid-cols-5 gap-3">
                    {steps.map((s, i) => (
                        <li key={i} className="relative lnl-card p-4">
                            <div className="w-8 h-8 grid place-items-center rounded-full bg-lnl-green text-white font-heading font-bold">{i + 1}</div>
                            <div className="mt-3 text-sm text-lnl-charcoal font-heading font-semibold leading-snug">{s}</div>
                        </li>
                    ))}
                </ol>
                <div className="mt-6">
                    <Link to="/admissions" className="lnl-btn-secondary" data-testid="view-admission-process">View Complete Admission Process</Link>
                </div>
            </div>
        </section>
    );
}

function TuitionCTA() {
    const [form, setForm] = useState({ full_name: "", mobile: "", email: "", request_type: "tuition", consent: false });
    const [sent, setSent] = useState(null);
    const [busy, setBusy] = useState(false);

    async function submit(kind) {
        if (!form.full_name || !form.email || !form.mobile) { toast.error("Please complete your name, mobile and email."); return; }
        if (!form.consent) { toast.error("Please give privacy consent to proceed."); return; }
        setBusy(true);
        try {
            const res = await submitTuitionLead({ ...form, request_type: kind });
            setSent(res.reference);
            toast.success(`Request received. Reference ${res.reference}`);
        } catch (e) { toast.error("Could not submit the request. Please try again."); }
        finally { setBusy(false); }
    }

    return (
        <section className="py-16 lg:py-20 bg-white" data-testid="tuition-cta">
            <div className="lnl-container grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
                <div>
                    <SectionHeading eyebrow="Plan your education" title="Tuition, scholarships and payment guidance" description="Request the latest tuition assessment, payment guidance and scholarship information from LNL Admissions. Amounts are personalized — we do not publish invented figures online." />
                    <div className="mt-6 flex flex-wrap gap-3">
                        <Link to="/tuition-scholarships" className="lnl-btn-primary" data-testid="tuition-info-btn">Request Tuition Information</Link>
                        <Link to="/tuition-scholarships#scholarships" className="lnl-btn-secondary" data-testid="scholarship-info-btn">Ask About Scholarships</Link>
                        <Link to="/contact" className="lnl-link inline-flex items-center py-2.5">Talk to Admissions</Link>
                    </div>
                </div>
                <div className="lnl-card p-6 bg-lnl-green-bg/40 border-lnl-green/20" data-testid="quick-lead-form">
                    <div className="lnl-eyebrow mb-2">Quick request</div>
                    <div className="font-heading font-bold text-xl text-lnl-charcoal">Get tuition & scholarship info</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                        <input aria-label="Full name" placeholder="Full name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="rounded-md border border-lnl-border bg-white px-3 py-2.5 sm:col-span-2" data-testid="tuition-name" />
                        <input aria-label="Mobile" placeholder="Mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="rounded-md border border-lnl-border bg-white px-3 py-2.5" data-testid="tuition-mobile" />
                        <input aria-label="Email" placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-md border border-lnl-border bg-white px-3 py-2.5" data-testid="tuition-email" />
                    </div>
                    <label className="mt-3 flex items-start gap-2 text-xs text-lnl-gray">
                        <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5" data-testid="tuition-consent" />
                        I agree to be contacted by LNL Admissions regarding this request.
                    </label>
                    <div className="mt-4 flex flex-wrap gap-2">
                        <button onClick={() => submit("tuition")} disabled={busy} className="lnl-btn-primary" data-testid="tuition-submit">Send tuition request</button>
                        <button onClick={() => submit("scholarship")} disabled={busy} className="lnl-btn-secondary" data-testid="scholarship-submit">Ask about scholarships</button>
                    </div>
                    {sent && (
                        <div className="mt-4 text-xs bg-white border border-lnl-green/30 rounded-md p-3 text-lnl-green-deep">
                            Received. Your reference: <b>{sent}</b>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

function CampusExperienceSection() {
    const cats = [
        "Classrooms", "Laboratories", "Library", "Student Activities", "Student Organizations",
        "Community Extension", "Academic Events", "Sports", "Guidance Services", "Campus Facilities",
    ];
    return (
        <section className="py-16 lg:py-20 bg-lnl-off-white" data-testid="campus-experience">
            <div className="lnl-container">
                <SectionHeading eyebrow="Campus experience" title="What life looks like at LNL" description="A snapshot of the campus categories you'll encounter — real photography is added by administrators." />
                <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {cats.map((c) => (
                        <div key={c} className="lnl-card p-4 text-center" data-testid={`campus-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}>
                            <div className="font-heading font-semibold text-sm text-lnl-charcoal">{c}</div>
                            <div className="text-[11px] text-lnl-gray mt-1">Photography by administrator</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function CommunitySpotlightSection({ stories }) {
    // Auto-hide the section when the moderation queue has produced nothing yet —
    // this way the homepage never renders an empty state, and it lights up the
    // moment a Super Admin approves the first Campus Life submission.
    if (!stories || stories.length === 0) return null;
    const featured = stories[0];
    const rest = stories.slice(1, 3);
    return (
        <section className="py-16 lg:py-20 bg-white" data-testid="community-spotlight">
            <div className="lnl-container">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <SectionHeading
                        eyebrow="Community Spotlight"
                        title="Stories from the LNL community"
                        description="Real photos and moments shared by our students, alumni, parents and staff — hand-picked by the LNL administration."
                    />
                    <Link to="/campus-life" className="lnl-link inline-flex items-center gap-1" data-testid="spotlight-see-all">
                        See more stories <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
                <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-4">
                    {/* Featured — spans 3 cols on lg+ */}
                    <article className="lnl-card overflow-hidden lg:col-span-3 flex flex-col" data-testid="spotlight-featured">
                        {featured.image_url ? (
                            <div className="aspect-[16/9] bg-lnl-off-white overflow-hidden">
                                <img
                                    src={campusImageUrl(featured.id)}
                                    alt={featured.caption || `Campus Life submission by ${featured.display_name}`}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                        ) : (
                            <div className="aspect-[16/9] bg-lnl-green-bg grid place-items-center">
                                <span className="text-lnl-green-deep font-heading font-bold text-sm uppercase tracking-widest">Community story</span>
                            </div>
                        )}
                        <div className="p-6 flex flex-col flex-1">
                            <div className="text-[11px] uppercase tracking-[0.16em] font-heading font-semibold text-lnl-green-deep">Featured story</div>
                            {featured.caption && (
                                <h3 className="font-heading font-bold text-lnl-charcoal text-xl mt-2 leading-snug">{featured.caption}</h3>
                            )}
                            {featured.story && (
                                <p className="text-sm text-lnl-charcoal/85 mt-2 leading-relaxed line-clamp-4">{featured.story}</p>
                            )}
                            <div className="mt-4 pt-4 border-t border-lnl-border flex items-center justify-between">
                                <div className="min-w-0">
                                    <div className="font-heading font-semibold text-sm text-lnl-charcoal truncate">{featured.display_name || "LNL Community"}</div>
                                    {featured.affiliation && (
                                        <div className="text-xs text-lnl-gray truncate">{featured.affiliation}{featured.grad_year ? ` · Class of ${featured.grad_year}` : ""}</div>
                                    )}
                                </div>
                                <Link to="/campus-life" className="lnl-btn-secondary text-xs" data-testid="spotlight-share">
                                    Share your story <ArrowRight className="h-3.5 w-3.5" />
                                </Link>
                            </div>
                        </div>
                    </article>

                    {/* Secondary column */}
                    <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
                        {rest.map((s) => (
                            <article key={s.id} className="lnl-card overflow-hidden flex" data-testid={`spotlight-secondary-${s.id}`}>
                                {s.image_url ? (
                                    <div className="w-28 sm:w-32 shrink-0 bg-lnl-off-white overflow-hidden">
                                        <img
                                            src={campusImageUrl(s.id)}
                                            alt={s.caption || `Campus Life submission by ${s.display_name}`}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    </div>
                                ) : (
                                    <div className="w-28 sm:w-32 shrink-0 bg-lnl-green-bg" />
                                )}
                                <div className="p-4 flex flex-col min-w-0 flex-1">
                                    {s.caption && (
                                        <div className="font-heading font-bold text-sm text-lnl-charcoal leading-snug line-clamp-2">{s.caption}</div>
                                    )}
                                    {s.story && (
                                        <p className="text-xs text-lnl-charcoal/85 mt-1 line-clamp-3">{s.story}</p>
                                    )}
                                    <div className="mt-auto pt-2 text-[11px] text-lnl-gray truncate">
                                        {s.display_name || "LNL Community"}
                                        {s.grad_year ? ` · '${s.grad_year}` : ""}
                                    </div>
                                </div>
                            </article>
                        ))}
                        {rest.length === 0 && (
                            <div className="lnl-card p-6 flex items-center justify-center text-center bg-lnl-off-white">
                                <div>
                                    <div className="text-sm font-heading font-semibold text-lnl-charcoal">Your story could be here.</div>
                                    <p className="text-xs text-lnl-gray mt-1">Share a moment from your LNL experience — approved submissions are featured on the homepage.</p>
                                    <Link to="/campus-life" className="lnl-btn-primary text-xs mt-3 inline-flex" data-testid="spotlight-empty-share">
                                        Share yours
                                    </Link>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

function TestimonialsSection() {
    return (
        <section className="py-14 bg-white" data-testid="testimonials">
            <div className="lnl-container">
                <SectionHeading eyebrow="Voices from the community" title="Student, parent and alumni stories" />
                <div className="mt-6 rounded-md border border-dashed border-lnl-border p-8 text-center bg-lnl-off-white">
                    <div className="font-heading font-semibold text-lnl-charcoal">Approved testimonials will appear here.</div>
                    <div className="text-sm text-lnl-gray mt-1">To protect student privacy, testimonials are shown only after written consent and administrator approval.</div>
                </div>
            </div>
        </section>
    );
}

function NewsSection({ news }) {
    return (
        <section className="py-16 lg:py-20 bg-lnl-off-white" data-testid="home-news">
            <div className="lnl-container">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                    <SectionHeading eyebrow="Latest updates" title="News, announcements and events" />
                    <Link to="/news" className="lnl-link inline-flex items-center gap-1" data-testid="see-all-news">See all <ArrowRight className="h-4 w-4" /></Link>
                </div>
                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {news.map((n) => (
                        <Link to={`/news/${n.slug}`} key={n.slug} className="lnl-card p-5 hover:border-lnl-green/40 transition-colors" data-testid={`news-card-${n.slug}`}>
                            <div className="text-[11px] uppercase tracking-[0.14em] font-heading font-semibold text-lnl-green-deep">{n.category}</div>
                            <div className="font-heading font-bold text-lg text-lnl-charcoal mt-1 leading-snug">{n.title}</div>
                            <div className="text-sm text-lnl-gray mt-2 line-clamp-3">{n.summary}</div>
                            <div className="mt-4 text-xs text-lnl-green-deep font-heading font-semibold inline-flex items-center gap-1">Read more <ArrowRight className="h-3.5 w-3.5" /></div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}

function StudentResourcesSection({ settings }) {
    if (!settings) return null;
    const items = [
        { label: "Student Portal", href: settings.portal_url, icon: BookOpen, external: true },
        { label: "Library", href: settings.library_url, icon: Library, external: true },
        { label: "Library OPAC", href: settings.opac_url, icon: Library, external: true },
        { label: "LNL Mobile App", href: "/mobile-app", icon: Smartphone },
        { label: "Academic Calendar", href: "/faq", icon: CalendarDays },
        { label: "Announcements", href: "/news", icon: Megaphone },
    ];
    return (
        <section className="py-16 lg:py-20 bg-white" data-testid="student-resources">
            <div className="lnl-container">
                <SectionHeading eyebrow="For current students" title="Quick access to student resources" />
                <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {items.map((it) => {
                        const Component = it.external ? "a" : Link;
                        const extraProps = it.external ? { href: it.href, target: "_blank", rel: "noopener noreferrer" } : { to: it.href };
                        return (
                            <Component key={it.label} {...extraProps} className="lnl-card p-4 flex flex-col items-start gap-2 hover:border-lnl-green/40" data-testid={`resource-${it.label.toLowerCase().replace(/\s+/g, "-")}`}>
                                <div className="w-9 h-9 rounded-md bg-lnl-green-bg text-lnl-green-deep grid place-items-center">
                                    <it.icon className="h-4 w-4" />
                                </div>
                                <div className="font-heading font-semibold text-sm text-lnl-charcoal">{it.label}</div>
                                <div className="text-[11px] text-lnl-gray">{it.external ? "Opens in new tab" : "View page"}</div>
                            </Component>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function CampusLocationSection({ settings }) {
    if (!settings) return null;
    const q = encodeURIComponent(settings.map_query || "Lyceum Northern Luzon Urdaneta");
    return (
        <section className="py-16 lg:py-20 bg-lnl-off-white" data-testid="campus-location">
            <div className="lnl-container grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div>
                    <SectionHeading eyebrow="Visit us" title="Visit Lyceum Northern Luzon" description="Come see the campus. Speak with Admissions. Get to know your future school." />
                    <div className="mt-5 space-y-2 text-sm">
                        <div className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-lnl-green" /> {settings.address_line}, {settings.province_line}</div>
                        <div className="flex items-center gap-2"><PhoneCall className="h-4 w-4 text-lnl-green" /> <a className="hover:underline" href={`tel:${settings.mobile.replace(/[^0-9+]/g, "")}`}>{settings.mobile}</a></div>
                    </div>
                    <div className="mt-3 text-xs text-lnl-gray">{settings.office_hours_draft}</div>
                    <div className="mt-6 flex flex-wrap gap-3">
                        <a href={`https://www.google.com/maps/search/?api=1&query=${q}`} target="_blank" rel="noopener noreferrer" className="lnl-btn-primary" data-testid="get-directions">Get Directions</a>
                        <Link to="/campus-visit" className="lnl-btn-secondary" data-testid="book-visit">Book a Campus Visit</Link>
                    </div>
                </div>
                <div className="rounded-md overflow-hidden border border-lnl-border">
                    <iframe
                        title="LNL location"
                        src={`https://www.google.com/maps?q=${q}&output=embed`}
                        width="100%" height="360" style={{ border: 0 }} loading="lazy" allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                        data-testid="campus-map"
                    />
                </div>
            </div>
        </section>
    );
}

function FinalCTA() {
    return (
        <section className="py-20 bg-lnl-green-deep" data-testid="final-cta">
            <div className="lnl-container text-center">
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white leading-tight">Ready to Begin Your LNL Journey?</h2>
                <p className="mt-3 text-white/85 max-w-xl mx-auto">Start an application, talk to Admissions, or review the exact requirements for your applicant path.</p>
                <div className="mt-8 flex flex-wrap gap-3 justify-center">
                    <Link to="/admissions/apply" className="lnl-btn-gold" data-testid="final-apply">Start Your Application</Link>
                    <Link to="/contact" className="lnl-btn-secondary bg-transparent border-white text-white hover:bg-white hover:text-lnl-green-deep" data-testid="final-talk">Talk to Admissions</Link>
                    <Link to="/admissions/requirements" className="text-white underline underline-offset-4 self-center" data-testid="final-requirements">View Requirements</Link>
                </div>
            </div>
        </section>
    );
}
